import { useTranslations } from 'next-intl';
import React, { Fragment } from 'react';
import useOfferComparisonStore, {
  priceSelector,
  selectPromoValue,
  selectTotalAnnualCostsInclPromo,
} from '../store/use-offer-comparison-store';
import { EuroSignPosition, formatPrice } from '../utils/format-price';
import useEnergyCalculatorStore from '../store/use-energy-calculator-store';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import useVatLabel from '../utils/vat-label';
import PriceBreakdownRow from './price-breakdown-row';
import PriceBreakdownLayout from './price-breakdown-layout';
import BreakdownHeader, {
  BasePriceData,
  PricePerKwhData,
} from './price-breakdown-header';

export default function PriceBreakdownGermany() {
  const t = useTranslations('priceBreakdown');
  const headingStyles = 'leading-8 font-bold text-lg pt-6 pb-4 ';
  const offer = useOfferComparisonStore((state) => state.offer);
  const netCosts = offer?.netCosts;
  const promo = offer?.promoDiscount;
  const customerType = useEnergyCalculatorStore((state) => state.customerType);
  const isPrivateCustomer = customerType === CustomerType.Home;
  const vat = useVatLabel(isPrivateCustomer);
  const promoValue = useOfferComparisonStore(selectPromoValue);
  const totalAnnualCostsInclPromo = useOfferComparisonStore(
    selectTotalAnnualCostsInclPromo
  );
  const offerState = useOfferComparisonStore();
  const calculatedPrice = (price: number) => priceSelector(offerState, price);
  const calculatedGermanBonus =
    netCosts?.totalAnnualCostExclDiscounts != null &&
    netCosts?.totalAnnualCostInclDiscounts != null
      ? calculatedPrice(
          netCosts.totalAnnualCostExclDiscounts -
            netCosts.totalAnnualCostInclDiscounts
        )
      : 0;

  const annualCostsExclDiscounts = calculatedPrice(
    netCosts?.totalAnnualCostExclDiscounts ?? 0
  );

  const kwhPriceInCents =
    priceSelector(offerState, netCosts?.pricePerkWh ?? 0) * 100;
  const monthlyBasePrice = calculatedPrice(netCosts?.energyBasePrice ?? 0) / 12;

  const pricePerKwh: PricePerKwhData = {
    label: `${t('proKwhTitle')} ${vat}`,
    value: `${formatPrice(kwhPriceInCents, true, EuroSignPosition.None)} ${t('cents')}`,
    labelTestId: isPrivateCustomer
      ? 'energyCalculatorIndividualOfferPricePerKwhPrivate'
      : 'energyCalculatorIndividualOfferPricePerKwhBusiness',
    valueTestId: isPrivateCustomer
      ? 'energyCalculatorIndividualOfferPriceCents'
      : 'energyCalculatorOfferPricePerKwhBusinessValue',
  };

  const basePricePerMonth: BasePriceData = {
    label: `${t('basePricePerMonthTitle')} ${vat}`,
    value: formatPrice(monthlyBasePrice, true, EuroSignPosition.Suffix),
  };

  const sections = [
    {
      heading: t('energyCosts'),
      rows: [
        {
          title: t('energyWorkingPrice'),
          value: netCosts?.energyWorkingPrice ?? 0,
        },
        {
          title: t('energyBasePrice'),
          value: netCosts?.energyBasePrice ?? 0,
        },
        {
          title: t('totalEnergyCosts'),
          value: netCosts?.totalAnnualCostExclDiscounts ?? 0,
          isHighlighted: true,
        },
      ],
    },
    {
      heading: t('total'),
      rows: [
        ...(calculatedGermanBonus > 0
          ? [
              {
                title: `${t('bonus')} ${vat}`,
                value: -calculatedGermanBonus,
              },
            ]
          : []),
        ...(promo && promo.value > 0
          ? [
              {
                title: `${promo.campaignName || promo.name} ${vat}`,
                value: -promoValue,
                testIdTitle: 'offerBreakdownCampaignTitle',
                testIdValue: 'offerBreakdownCampaignValue',
              },
            ]
          : []),
        {
          title: `${t('totalCosts')} ${vat}`,
          value: totalAnnualCostsInclPromo ?? 0,
          isHighlighted: true,
          testIdTitle: 'energyCalculatorIndividualOffer1YCostsPrivate',
          testIdValue: 'energyCalculatorIndividualOfferTotal1YCosts',
        },
        {
          title: `${t('totalCostsExclDiscounts')} ${vat}`,
          value:
            annualCostsExclDiscounts === totalAnnualCostsInclPromo
              ? 0
              : annualCostsExclDiscounts,
          testIdTitle: 'offerBreakdownAnnualTotalTitle',
          testIdValue: 'offerBreakdownAnnualTotalValue',
        },
      ],
    },
  ];

  return (
    <PriceBreakdownLayout
      header={
        <BreakdownHeader
          pricePerKwh={pricePerKwh}
          basePricePerMonth={basePricePerMonth}
        />
      }
    >
      <div>
        {sections.map((section) => (
          <Fragment key={section.heading}>
            <p
              data-testid="electricity-costs-heading"
              className={headingStyles}
            >
              {section.heading}
            </p>
            {section.rows.map((row) => (
              <PriceBreakdownRow key={row.title} {...row} />
            ))}
          </Fragment>
        ))}
      </div>
    </PriceBreakdownLayout>
  );
}
